<template>
    <header class="header" :class="headerClasses">
        <div class="header__wrapper">
            <component :is="logoTag"
                       class="header__logo"
                       :to="logoHref"
            >
                <img src="/images/logo.svg"
                     alt=""
                />
            </component>

            <HeaderNavigation v-if="isUserAuth && !isMobileOrTablet"
                              class="header__navigation"
                              :is-menu-open="isMenuOpen"
                              :navigation="navigation"
                              @update-menu-state="onUpdateMenuState"
            />

            <div v-if="!isMobileOrTablet" class="header__buttons">
                <template v-if="isUserAuth">
                    <UiButton color="secondary"
                              :to="profileLink"
                              icon-position="right"
                              name="user"
                    >
                        Профиль
                    </UiButton>
                </template>
                <template v-else>
                    <UiButton to="/login"
                              color="primary"
                    >
                        Войти
                    </UiButton>
                </template>
            </div>

            <HeaderMobileMenu v-if="isMobileOrTablet" class="header__mobile-menu" />
        </div>
    </header>
</template>

<script setup lang="ts">
    import type {
        HeaderNavigationItemType,
    } from '~/src/widgets/header/ui/HeaderNavigation/types/types'
    import { useUserStore } from '~/src/shared/pinia/user/useUserStore'
    import { useWindowStore } from '~/src/shared/pinia/useWindowStore'
    import HeaderMobileMenu from './ui/HeaderMobileMenu/HeaderMobileMenu.vue'
    import HeaderNavigation from './ui/HeaderNavigation/HeaderNavigation.vue'

    defineProps<{
        logoLink?: boolean
    }>()

    const windowStore = useWindowStore()
    const isMobile = computed(() => windowStore.isMobile)
    const isMobileOrTablet = computed(() => windowStore.isMobileOrTablet)

    const isMenuOpen = ref(false)

    const headerClasses = computed(() => ({ '_menu-open': isMenuOpen.value }))

    const userStore = useUserStore()
    const isUserAuth = computed(() => userStore.isUserAuth)

    const navigation: HeaderNavigationItemType[] = [
        {
            id: 'instruments',
            label: 'Инструменты',
            link: undefined,
            menu: [
                {
                    title: 'Инструменты',
                    links: [
                        {
                            label: 'Мониторинг позиций',
                            href: '/monitoring',
                        },
                        {
                            label: 'Поиск трафика',
                            href: '/analyze',
                        },
                        {
                            label: 'Анализ блогеров',
                            href: '/analyze-sku',
                        },
                        {
                            label: 'Редирект',
                            href: ''
                        }
                    ],
                },
                {
                    title: 'ИИ-помощник',
                    links: [
                        {
                            label: 'Описание карточек',
                            href: '',
                        }
                    ],
                },
            ],
        },
        {
            id: 'blog',
            label: 'Блог',
            link: '/blog',
        },
        {
            id: 'tariffs',
            label: 'Тарифы',
            link: '/profile/tariff',
        }
    ]

    function onUpdateMenuState(value: boolean) {
        isMenuOpen.value = value
    }

    const logoTag = resolveComponent('nuxt-link')

    const logoHref = computed(() => '/')

    const profileLink = computed(() => isMobile.value ? '/profile' : '/profile/edit')
</script>

<style lang="scss" scoped>
    .header {
        position: relative;
        z-index: 10;
        padding: 16px 80px;
        background-color: var(--header-background-color);
        border-bottom-left-radius: 40px;
        border-bottom-right-radius: 40px;

        @include respond-to(tablet) {
            border-bottom-left-radius: 32px;
            border-bottom-right-radius: 32px;
        }

        @include respond-to(mobile) {
            display: flex;
            align-items: center;
            height: 52px;
            padding: 12px 24px;
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
        }

        &._menu-open {
            z-index: 1002;
        }

        &__logo {
            display: flex;

            img {
                max-width: 100%;
                height: auto;
            }

            @include respond-to(mobile) {
                width: 149px;
            }
        }

        &__wrapper {
            display: flex;
            align-items: center;
            width: 100%;
        }

        &__navigation {
            display: flex;
            justify-content: space-between;
            margin: 0 auto;

            @include respond-to(tablet) {
                display: none;
            }
        }

        &__buttons {
            display: flex;
            align-items: center;
            margin-left: auto;

            & > *:not(:first-child) {
                margin-left: 12px;
            }
        }

        &__mobile-menu {
            display: none;

            @include respond-to(tablet) {
                display: flex;
                margin-left: auto;
            }
        }
    }
</style>
